import { lazy, Suspense, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { fbt } from 'fbtee'
import { Button } from './ui/button'
import { HeaderLogo } from './HeaderLogo'
import { RenameWordmark } from './RenameWordmark'
import { UserMenu } from './UserMenu'
import { HeaderNav } from './HeaderNav'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { useTransitionNavigate, popPreviousPath } from '@/lib/viewTransition'
import { cn } from '@/lib/utils'
import { useHeaderHeightVar } from '@/hooks/useHeaderHeightVar'
import { useAtTop } from '@/hooks/useAtTop'
import { navPillClass } from '@/lib/glass'

const WhatsNewSlot = lazy(() => import('./WhatsNewSlot').then(m => ({ default: m.WhatsNewSlot })))

const EASE = 'duration-[400ms] ease-[cubic-bezier(0.25,0.8,0.25,1)] motion-reduce:transition-none'

// Mobile: the header has no bar of its own. Its controls float, each on its own glass
// pill like the FABs, and stay put while the page scrolls. It's click-through on mobile;
// the pills take taps (chrome-hit, via navPillClass). Desktop keeps a plain solid header:
// its nav links aren't pills.
const HEADER_CLASS = cn(
  // Not the chrome-passthrough class: as unlayered CSS it would beat md:pointer-events-auto.
  'group/hdr pointer-events-none',
  'md:pointer-events-auto md:border-b md:bg-background',
  // Desktop only: a view-transition-name makes the header a backdrop root, which on
  // mobile would cut the floating pills' blur off from the page behind them.
  'md:[view-transition-name:app-header]',
)

// While the header sits flush at the top of the page, its pills shed their glass so
// the row melts into the page background; they pick it up as soon as content passes
// behind. blur(0px), not none, so the blur animates instead of popping.
const PILL_CLASS = cn(
  navPillClass,
  'rounded-full transition-[background-color,border-color,box-shadow,backdrop-filter]',
  EASE,
  'group-data-[at-top]/hdr:border-transparent group-data-[at-top]/hdr:bg-transparent group-data-[at-top]/hdr:shadow-none group-data-[at-top]/hdr:backdrop-blur-[0px]',
  'md:border-transparent md:bg-transparent md:shadow-none md:backdrop-blur-[0px]',
)


export interface AppHeaderProps {
  back?: boolean | string
  title?: React.ReactNode
  /**
   * Slot beside the logo: left edge on mobile (the logo stays centred), right
   * after the logo on desktop. Only used when there's no back button.
   */
  leading?: React.ReactNode
  /**
   * Right-aligned slot, visible on all breakpoints (sits before desktop nav).
   * Left empty on a tab root's Faro header, it holds the What's new pill.
   */
  action?: React.ReactNode
  /** Suppress the desktop cross-app nav + account menu (e.g. in festival mode). */
  hideNav?: boolean
  /** Forwarded to HeaderLogo so the admin clock-override popover can jump the schedule day. */
  /** Show animated clock hands on the logo (festival mode). */
  showClock?: boolean
  /** Short day label (e.g. "THU") shown as a badge on the clock logo. */
  dayLabel?: string
  /** Hide the header on mobile, where it would hold only the logo (Search, Crew, Profile). */
  hideOnMobile?: boolean
}

export function AppHeader({ back, title, leading, action, hideNav, showClock, dayLabel, hideOnMobile }: AppHeaderProps) {
  const navigate = useTransitionNavigate()
  const online = useOnlineStatus()
  const headerRef = useRef<HTMLElement>(null)
  useHeaderHeightVar(headerRef)
  const atTop = useAtTop()
  if (!action && !back && !hideNav && !hideOnMobile) {
    action = <Suspense fallback={null}><WhatsNewSlot /></Suspense>
  }

  function handleBack() {
    const fallback = typeof back === 'string' ? back : '/'
    const target = popPreviousPath() ?? fallback
    navigate(target, { replace: true, direction: 'back' })
  }

  return (
    <header
      ref={headerRef}
      data-at-top={atTop || undefined}
      className={cn('sticky top-0 z-[60] pt-[env(safe-area-inset-top)]', HEADER_CLASS, hideOnMobile && 'max-md:hidden')}
    >
      <div
        className={cn(
          // Floating pills carry their own padding, so the row hugs the edges more.
          'relative px-4 py-2.5 flex items-center gap-3 md:px-6',
          leading && !back && 'min-h-15',
        )}
      >
      {back ? (
        <Button
          variant="ghost"
          size="icon"
          className={cn(PILL_CLASS, 'size-10')}
          onClick={handleBack}
          aria-label={fbt('Go back', 'Accessible label for the back navigation button')}
        >
          <ArrowLeft className="size-4" />
        </Button>
      ) : (
        <div className={cn(
          'flex items-center',
          PILL_CLASS,
          hideNav ? 'p-1' : 'gap-2 py-1 pl-1 pr-3.5',
          hideNav ? '' : 'mx-auto md:mx-0',
          leading && 'absolute left-1/2 -translate-x-1/2 md:static md:translate-x-0',
        )}>
          <HeaderLogo small showClock={showClock} dayLabel={dayLabel} />
          {!hideNav && (
            <Link to="/" replace className={cn('text-lg font-semibold tracking-tight', !online && 'text-muted-foreground')}>
              <RenameWordmark />
            </Link>
          )}
        </div>
      )}
        {leading && !back && <div className={cn('relative z-10 min-w-0 px-1.5 [&_button]:ml-0 [&_button]:max-w-[calc(50vw-6.5rem)]', PILL_CLASS)}>{leading}</div>}
        {title && <div className="flex-1 min-w-0"><div className={cn('w-fit max-w-full px-4 py-1.5', PILL_CLASS)}>{title}</div></div>}
        <div className={cn("ml-auto items-center gap-3", action ? "flex" : "hidden md:flex")}>
          {action && <div className={cn('flex items-center p-px empty:hidden', PILL_CLASS)}>{action}</div>}
          {!hideNav && (
            <div className="hidden md:flex items-center gap-3">
              <HeaderNav />
              <UserMenu />
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
